import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, BadgeCheck, Globe, Link as LinkIcon, Pencil, Plus, X } from "lucide-react";
import Button from "../Common/Button";
import { validateFormat, normalizeHandleValue } from "../../utils/socialValidation";
import { buildPlatformProfileUrl } from "../../utils/socialVerification";
import { startSocialVerification, isPlatformVerifiable } from "../../api/socialVerificationApi";
import { savePendingSocialVerification } from "../../utils/socialVerificationSession";

const PlatformIcon = ({ icon, name }) => {
    if (!icon) return <Globe className="h-5 w-5 text-slate-400" />;
    return (
        <img
            src={icon}
            alt={name}
            className="h-6 w-6 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
    );
};

const isValidUrl = (value) => /^https?:\/\/\S+$/i.test((value || "").trim());

const SocialModal = ({
    isOpen,
    onClose,
    platforms = [],
    handles = {},
    links = {},
    onHandleChange,
    onLinkChange,
    onRemoveEntry,
    showVerification = false,
    verifiedPlatforms = {},
    onBeforeVerify,
    allowEditExisting = true,
    errorMessage = "",
}) => {
    const { t } = useTranslation();
    const [activePlatformKey, setActivePlatformKey] = useState(null);
    // null = adding a new account for this platform; a number = editing the
    // entry at that index in handles[platformKey]/links[platformKey].
    const [activeIndex, setActiveIndex] = useState(null);
    const [draftHandle, setDraftHandle] = useState("");
    const [draftLink, setDraftLink] = useState("");

    const activePlatform = useMemo(
        () => platforms.find((platform) => platform.key === activePlatformKey) || null,
        [platforms, activePlatformKey]
    );

    useEffect(() => {
        if (!isOpen) {
            setActivePlatformKey(null);
            setActiveIndex(null);
            setDraftHandle("");
            setDraftLink("");
        }
    }, [isOpen]);

    const openPlatformEditor = (platformKey, index = null) => {
        setActivePlatformKey(platformKey);
        setActiveIndex(index);
        const existingHandle = index != null ? ((handles[platformKey] || [])[index] || "") : "";
        const existingLink = index != null ? ((links[platformKey] || [])[index] || "") : "";
        setDraftHandle(existingHandle);
        setDraftLink(existingLink || buildPlatformProfileUrl(platformKey, existingHandle));
    };

    const handleBackToList = () => {
        setActivePlatformKey(null);
        setActiveIndex(null);
        setDraftHandle("");
        setDraftLink("");
    };

    const isDuplicateHandle = (platformKey, handle, ignoreIndex) =>
        (handles[platformKey] || []).some(
            (existing, index) => index !== ignoreIndex && normalizeHandleValue(existing) === normalizeHandleValue(handle)
        );

    const handleSavePlatform = () => {
        if (!activePlatform) return;
        const nextHandle = draftHandle.trim();
        const nextLink = draftLink.trim();

        if (!nextHandle || !nextLink) return;
        if (!validateFormat(activePlatform.key, nextHandle)) return;
        if (!isValidUrl(nextLink)) return;
        if (isDuplicateHandle(activePlatform.key, nextHandle, activeIndex)) return;

        onHandleChange(activePlatform.key, activeIndex, nextHandle);
        onLinkChange(activePlatform.key, activeIndex, nextLink);
        handleBackToList();
    };

    const handleRemoveEntry = (event, platformKey, index) => {
        event.stopPropagation();
        onRemoveEntry(platformKey, index);
    };

    const handleVerify = async (event, platform, index) => {
        event.stopPropagation();
        const handle = ((handles[platform.key] || [])[index] || "").trim();
        onBeforeVerify?.();
        savePendingSocialVerification({ platform: platform.key, handle, index });
        const result = await startSocialVerification({ platform: platform.key, handle, returnTo: "/register", index });
        if (result?.redirectUrl) {
            window.location.href = result.redirectUrl;
        }
    };

    if (!isOpen) return null;

    const isDraftHandleValid = activePlatform
        ? validateFormat(activePlatform.key, draftHandle.trim())
        : false;
    const isDraftHandleDuplicate = activePlatform
        ? isDuplicateHandle(activePlatform.key, draftHandle.trim(), activeIndex)
        : false;
    const canSaveDraft =
        draftHandle.trim() &&
        draftLink.trim() &&
        isDraftHandleValid &&
        isValidUrl(draftLink) &&
        !isDraftHandleDuplicate;
    const hasAtLeastOneSocial = platforms.some((platform) =>
        (handles[platform.key] || []).some(
            (handle, index) => (handle || "").trim() && ((links[platform.key] || [])[index] || "").trim()
        )
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 sm:px-6 sm:py-10">
            <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.2)] md:max-w-4xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 z-40 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow focus:outline-none"
                >
                    <X className="h-5 w-5 text-primary" />
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative order-last h-48 w-full overflow-hidden sm:h-56 md:order-last md:h-auto">
                        <img
                            src="/assets/images/common/social-popup.png"
                            alt=""
                            className="h-full w-full object-cover"
                        />
                    </div>

                    {!activePlatform ? (
                        <div className="order-first flex h-[520px] flex-col overflow-hidden p-6 sm:p-8 md:p-12">
                        <h2 className="text-xl font-semibold text-slate-900">{t("AUTH_REGISTER_SHARE_SOCIALS")}</h2>
                        <p className="mt-2 text-sm text-slate-500">
                            {t("AUTH_SOCIAL_MODAL_SUBTITLE")}
                        </p>
                        {errorMessage ? (
                            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                                {errorMessage}
                            </p>
                        ) : null}

                        <div className="mt-6 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                            {platforms.map((platform) => {
                                const entryHandles = handles[platform.key] || [];
                                const entryLinks = links[platform.key] || [];
                                const entryVerified = verifiedPlatforms[platform.key] || [];
                                const hasEntries = entryHandles.some((handle) => (handle || "").trim());

                                if (!hasEntries) {
                                    const canEditPlatform = allowEditExisting;
                                    return (
                                        <div
                                            key={platform.key}
                                            onClick={canEditPlatform ? () => openPlatformEditor(platform.key, null) : undefined}
                                            onKeyDown={canEditPlatform ? (event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    openPlatformEditor(platform.key, null);
                                                }
                                            } : undefined}
                                            role={canEditPlatform ? "button" : undefined}
                                            tabIndex={canEditPlatform ? 0 : undefined}
                                            className={`w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-left ${
                                                canEditPlatform ? "cursor-pointer" : "cursor-default"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                                    <PlatformIcon icon={platform.icon} name={platform.name} />
                                                    <span>{platform.name}</span>
                                                </div>
                                                <Plus className="h-4 w-4 text-slate-500" />
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={platform.key} className="space-y-2">
                                        {entryHandles.map((handle, index) => {
                                            if (!(handle || "").trim()) return null;
                                            const link = entryLinks[index] || "";
                                            const verified = Boolean(entryVerified[index]);
                                            const canEditEntry = allowEditExisting && !verified;

                                            return (
                                                <div
                                                    key={`${platform.key}-${index}`}
                                                    onClick={canEditEntry ? () => openPlatformEditor(platform.key, index) : undefined}
                                                    onKeyDown={canEditEntry ? (event) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            openPlatformEditor(platform.key, index);
                                                        }
                                                    } : undefined}
                                                    role={canEditEntry ? "button" : undefined}
                                                    tabIndex={canEditEntry ? 0 : undefined}
                                                    className={`w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-left ${
                                                        canEditEntry ? "cursor-pointer" : "cursor-default"
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                                                <PlatformIcon icon={platform.icon} name={platform.name} />
                                                                <span className="truncate">{handle}</span>
                                                                {verified && (
                                                                    <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-600">
                                                                        <BadgeCheck className="h-4 w-4 shrink-0" />
                                                                        {t("AUTH_SOCIAL_MODAL_VERIFIED_LABEL", "Verified")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {link.trim() ? (
                                                                <p className="mt-1 truncate text-xs text-slate-500">
                                                                    {link}
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                        {allowEditExisting ? (
                                                            <div className="flex shrink-0 items-center gap-2">
                                                                {!verified && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(event) => { event.stopPropagation(); openPlatformEditor(platform.key, index); }}
                                                                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                                                        aria-label={t("COMMON_EDIT", "Edit")}
                                                                        title={t("COMMON_EDIT", "Edit")}
                                                                    >
                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                    </button>
                                                                )}
                                                                {showVerification && !verified && isPlatformVerifiable(platform.key) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(event) => handleVerify(event, platform, index)}
                                                                        className="text-xs font-medium text-primary underline underline-offset-2 hover:opacity-75 focus:outline-none"
                                                                    >
                                                                        {t("AUTH_SOCIAL_MODAL_VERIFY")}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={(event) => handleRemoveEntry(event, platform.key, index)}
                                                                    className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                                                    aria-label={t("COMMON_REMOVE", "Remove")}
                                                                    title={t("COMMON_REMOVE", "Remove")}
                                                                >
                                                                    <X className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {allowEditExisting && (
                                            <button
                                                type="button"
                                                onClick={() => openPlatformEditor(platform.key, null)}
                                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-xs font-semibold text-primary hover:bg-slate-50 focus:outline-none"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                {t("AUTH_SOCIAL_MODAL_ADD_ANOTHER", { platform: platform.name })}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-auto pt-6">
                            <Button
                                type="button"
                                onClick={onClose}
                                disabled={!hasAtLeastOneSocial}
                                className="w-full"
                            >
                                {t("COMMON_SAVE")}
                            </Button>
                        </div>
                        </div>
                    ) : (
                        <div className="order-first flex h-[520px] flex-col overflow-hidden p-6 sm:p-8 md:p-12">
                        <button
                            type="button"
                            onClick={handleBackToList}
                            className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-primary focus:outline-none"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t("COMMON_BACK")}
                        </button>
                        <h2 className="text-xl font-semibold text-slate-900">{activePlatform.name} {t("AUTH_SOCIAL_MODAL_ACCOUNT")}</h2>
                        <p className="mt-2 text-sm text-slate-500">
                            {t("AUTH_SOCIAL_MODAL_EDITOR_SUBTITLE")}
                        </p>

                        <div className="mt-6 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-input-bg px-4 py-3">
                                <PlatformIcon icon={activePlatform.icon} name={activePlatform.name} />
                                <input
                                    type="text"
                                    value={draftHandle}
                                    onChange={(event) => setDraftHandle(event.target.value)}
                                    placeholder={`${activePlatform.name} ${t("AUTH_SOCIAL_MODAL_HANDLE_PLACEHOLDER")}`}
                                    className="w-full bg-transparent text-sm text-slate-700 outline-none focus:outline-none"
                                />
                                {draftHandle.trim() ? (
                                    <button
                                        type="button"
                                        onClick={() => setDraftHandle("")}
                                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>

                            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-input-bg px-4 py-3">
                                <LinkIcon className="h-4 w-4 text-slate-500" />
                                <input
                                    type="url"
                                    value={draftLink}
                                    onChange={(event) => setDraftLink(event.target.value)}
                                    placeholder={t("AUTH_SOCIAL_MODAL_URL_PLACEHOLDER")}
                                    className="w-full bg-transparent text-sm text-slate-700 outline-none focus:outline-none"
                                />
                                {draftLink.trim() ? (
                                    <button
                                        type="button"
                                        onClick={() => setDraftLink("")}
                                        className="flex h-6 w-6 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>

                            {draftHandle.trim() && !isDraftHandleValid ? (
                                <p className="text-xs text-red-500">
                                    {t("AUTH_SOCIAL_MODAL_INVALID_FORMAT")} {activePlatform.name}
                                </p>
                            ) : null}
                            {draftHandle.trim() && isDraftHandleValid && isDraftHandleDuplicate ? (
                                <p className="text-xs text-red-500">
                                    {t("AUTH_SOCIAL_MODAL_DUPLICATE_HANDLE")}
                                </p>
                            ) : null}
                            {draftLink.trim() && !isValidUrl(draftLink) ? (
                                <p className="text-xs text-red-500">{t("AUTH_SOCIAL_MODAL_INVALID_URL")}</p>
                            ) : null}

                        </div>

                        <div className="mt-auto pt-6">
                            <Button
                                type="button"
                                onClick={handleSavePlatform}
                                disabled={!canSaveDraft}
                                className="w-full"
                            >
                                {t("COMMON_SAVE")}
                            </Button>
                        </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SocialModal;
